import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { classifySeverity, SEVERITY_ROWS } from "./AxisPropertiesFields";
import {
  createLabel,
  type LidarClearanceLabel,
  updateLabel,
} from "./labelsApi";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

// Criticality isn't stored on the label - it's derived from the severity
// bands already configured on the axes it's linked to, so it always stays
// in sync with axis edits. Multi-axis labels show the most severe band.
function resultingCriticality(
  axes: LidarClearanceAxis[],
  axisValues: Record<number, number>
): string {
  const matchedRows = axes
    .map((axis) => classifySeverity(axis, axisValues[axis.id] ?? 0))
    .filter((row): row is (typeof SEVERITY_ROWS)[number] => row !== null);

  const worst = SEVERITY_ROWS.find((row) => matchedRows.includes(row));
  return worst?.label ?? t.SeverityNoIssueLabel;
}

type Props = {
  open: boolean;
  axes: LidarClearanceAxis[];
  region: string;
  systemId: number;
  initial: LidarClearanceLabel | null;
  suggestedNextCode: number;
  onClose: () => void;
  onSaved: (label: LidarClearanceLabel) => void;
};

export default function LabelEditDialog({
  open,
  axes,
  region,
  systemId,
  initial,
  suggestedNextCode,
  onClose,
  onSaved,
}: Props) {
  const [code, setCode] = useState(0);
  const [name, setName] = useState("");
  const [axisValues, setAxisValues] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode(initial?.label ?? suggestedNextCode);
    setName(initial?.label_name ?? "");
    setAxisValues(
      Object.fromEntries(
        (initial?.axis_values ?? []).map((entry) => [
          entry.axis_id,
          entry.axis_value,
        ])
      )
    );
  }, [open, initial, suggestedNextCode]);

  const canSave = code >= 0 && code <= 255 && !saving;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Values left at 0 are omitted - a missing axis already means 0.
      const payload = Object.entries(axisValues)
        .filter(([, value]) => value !== 0)
        .map(([axisId, value]) => ({
          axis_id: Number(axisId),
          axis_value: value,
        }));

      const saved = initial
        ? await updateLabel(region, systemId, initial.id, {
            label: code,
            label_name: name.trim() || null,
            axis_values: payload,
          })
        : await createLabel(region, systemId, code, name.trim() || null, payload);
      onSaved(saved);
      onClose();
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.saveLabel" },
      });
      toast.error(t.FailedToSaveLabel);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => (saving ? undefined : onClose())} fullWidth>
      <DialogTitle>
        {initial ? t.EditLabelDialogTitle : t.CreateLabelDialogTitle}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t.LabelCodeLabel}
              type="number"
              value={code}
              onChange={(event) => setCode(Number(event.target.value))}
              disabled={saving}
              slotProps={{ htmlInput: { min: 1, max: 255 } }}
              fullWidth
            />
            <TextField
              label={t.LabelNameLabel}
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={saving}
              fullWidth
            />
          </Stack>
          {axes.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary">
                {t.LabelAxisValuesHint}
              </Typography>
              {axes.map((axis) => (
                <TextField
                  key={axis.id}
                  label={axis.name_en}
                  type="number"
                  value={axisValues[axis.id] ?? 0}
                  onChange={(event) =>
                    setAxisValues((prev) => ({
                      ...prev,
                      [axis.id]: Number(event.target.value),
                    }))
                  }
                  disabled={saving}
                  slotProps={{
                    htmlInput: { min: axis.min_value, max: axis.max_value },
                  }}
                  fullWidth
                />
              ))}
              <Typography variant="body2" color="text.secondary">
                {t.ResultingCriticalityLabel}:{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {resultingCriticality(axes, axisValues)}
                </Typography>
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t.CancelButton}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          {t.SaveButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
